import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Label, TextInput } from "flowbite-react";
import { useGetData } from "../../../../../hooks/useGetData";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechasInterface } from "../../../../../interfaces/FechasInterface";
import { PadresHermanosInterface } from "../../../../../interfaces/PadresHermanosInterface";
import { ConyugeInterface } from "../../../../../interfaces/ConyugeInterface";
import { HijosInterface } from "../../../../../interfaces/HijosInterface";
import { NotasInfofamInterface } from "../../../../../interfaces/NotasInfofamInterface";
import HeaderInfoFam from "../../../../../img/Header_InfoFam.png";
import BarraInfoFam from "../../../../../img/Barrera_InfoFam.png";
import HeaderPrint from "../../../../../components/HeaderFormsPrint/HeaderFormsPrint";
import fotoMapa from "../../../../../img/fotoMapa.png";

export const InfoFamiliarPrint = () => {
  const tituloFormulario = "INFORMACIÓN FAMILIAR";
  const { control, handleSubmit, setValue, reset } = useForm();
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const [edad, setEdad] = useState<number | null>(null);
  const [fechaNacimiento, setFechaNacimiento] = useState<string>("");

  const formFieldsConyuge = [
    { label: "NOMBRE:", id: "nombrec" },
    { label: "EDAD:", id: "edadc" },
    { label: "DOMICILIO:", id: "domicilioc" },
    { label: "VIVE CON EL EVALUADO:", id: "vive-con-evaluadoc" },
    { label: "ACTIVIDAD LABORAL:", id: "actividad-laboralc" },
    { label: "ÚLTIMO GRADO DE ESTUDIOS:", id: "ultimo-grado-estudiosc" },
  ];

  const formFieldsHijos = [
    { label: "NOMBRE:", id: "nombreh" },
    { label: "EDAD:", id: "edadh" },
    { label: "DOMICILIO:", id: "domicilioh" },
    { label: "VIVE CON EL EVALUADO:", id: "vive-con-evaluadoh" },
    { label: "ÚLTIMO GRADO DE ESTUDIOS:", id: "ultimo-grado-estudiosh" },
  ];

  const [formData, setFormData] = useState({});

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const calcularEdad = (fechaNacimiento: string) => {
    const fechaNac = new Date(fechaNacimiento);
    const hoy = new Date();
    let edadCalculada = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
      edadCalculada--;
    }

    return edadCalculada;
  };

  const handleFechaNacimientoChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nuevaFechaNacimiento = event.target.value;
    setFechaNacimiento(nuevaFechaNacimiento);

    const nuevaEdad = calcularEdad(nuevaFechaNacimiento);
    setEdad(nuevaEdad);
  };

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const queryFecha = useGetData<FechasInterface[]>({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    name: ["fecha-visita-bienesinmuebles"],
  });

  const queryPadresHermanos = useGetData<PadresHermanosInterface[]>({
    url: `/padres-hermanos/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "padres-hermanos"],
  });

  const queryConyuge = useGetData<ConyugeInterface[]>({
    url: `/conyuge/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "conyuge"],
  });

  const queryHijos = useGetData<HijosInterface>({
    url: `/hijos/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "hijos"],
  });

  const queryNotasInfoFam = useGetData<NotasInfofamInterface[]>({
    url: `/notas_infofam/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "notas_infofam"],
  });

  const generoMap: { [key: string]: string } = {
    M: "Masculino",
    F: "Femenino",
    O: "Otro",
  };

  const generoTexto =
    generoMap[queryEvaluado.data?.genero as string] || "No especificado";

  useEffect(() => {
    if (queryEvaluado.data) {
      const fechaNac = queryEvaluado.data.fecha_nacimiento || "";
      setFechaNacimiento(fechaNac);

      const nuevaEdad = calcularEdad(fechaNac);
      setEdad(nuevaEdad);

      reset({
        nombre: queryEvaluado.data.nombre || "",
        apellido_paterno: queryEvaluado.data.apellido_paterno || "",
        apellido_materno: queryEvaluado.data.apellido_materno || "",
        rfc: queryEvaluado.data.rfc || "",
        genero: queryEvaluado.data.genero || "",
        estado_civil: queryEvaluado.data.estado_civil || "",
        escolaridad: queryEvaluado.data.escolaridad || "",
        fecha_nacimiento: queryEvaluado.data.fecha_nacimiento || "",
        curp: queryEvaluado.data.curp || "",
        empresa: queryEvaluado.data.empresa?.nombre || "",
      });
    }
  }, [queryEvaluado.data, reset]);

  useEffect(() => {
    if (queryPadresHermanos.data && queryPadresHermanos.data.length > 0) {
      const ph = queryPadresHermanos.data[0];
      reset({
        nombre_ph: ph.nombre_ph1 || "",
        parentesco_ph: ph.parentesco_ph1 || "",
        edad_ph: ph.edad_ph1 || "",
        estado_civilph: ph.estado_civilph1 || "",
        domicilio_ph: ph.domicilio_ph1 || "",
        vivecon_evaluadoph: ph.vivecon_evaluadoph1 || "",
        actividad_laboralph: ph.actividad_laboralph1 || "",
        ultimogrado_estudiosph: ph.ultimogrado_estudiosph1 || "",
      });
    }
  }, [queryPadresHermanos.data, reset]);

  useEffect(() => {
    if (queryConyuge.data && queryConyuge.data.length > 0) {
      const conyuge = queryConyuge.data[0];
      reset({
        nombrec: conyuge.nombrec || "",
        edadc: conyuge.edadc || "",
        domicilioc: conyuge.domicilioc || "",
        vivecon_evaluadoc: conyuge.vivecon_evaluadoc || "",
        actividad_laboralc: conyuge.actividad_laboralc || "",
        ultimogrado_estudiosc: conyuge.ultimogrado_estudiosc || "",
      });
    }
  }, [queryConyuge.data, reset]);

  useEffect(() => {
    if (queryHijos.data) {
      const hijo = queryHijos.data;
      reset({
        nombreh1: hijo.nombreh1 || "",
        edadh1: hijo.edadh1 || "",
        domicilioh1: hijo.domicilioh1 || "",
        vivecon_evaluadoh1: hijo.vivecon_evaluadoh1 || "",
        actividad_laboralh1: hijo.actividad_laboralh1 || "",
        ultimogrado_estudiosh1: hijo.ultimogrado_estudiosh1 || "",
        nombreh2: hijo.nombreh2 || "",
        edadh2: hijo.edadh2 || "",
        domicilioh2: hijo.domicilioh2 || "",
        vivecon_evaluadoh2: hijo.vivecon_evaluadoh2 || "",
        actividad_laboralh2: hijo.actividad_laboralh2 || "",
        ultimogrado_estudiosh2: hijo.ultimogrado_estudiosh2 || "",
        nombreh3: hijo.nombreh3 || "",
        edadh3: hijo.edadh3 || "",
        domicilioh3: hijo.domicilioh3 || "",
        vivecon_evaluadoh3: hijo.vivecon_evaluadoh3 || "",
        actividad_laboralh3: hijo.actividad_laboralh3 || "",
        ultimogrado_estudiosh3: hijo.ultimogrado_estudiosh3 || "",
        nombreh4: hijo.nombreh4 || "",
        edadh4: hijo.edadh4 || "",
        domicilioh4: hijo.domicilioh4 || "",
        vivecon_evaluadoh4: hijo.vivecon_evaluadoh4 || "",
        actividad_laboralh4: hijo.actividad_laboralh4 || "",
        ultimogrado_estudiosh4: hijo.ultimogrado_estudiosh4 || "",
      });
    }
  }, [queryHijos.data, reset]);

  useEffect(() => {
    if (queryNotasInfoFam.data && queryNotasInfoFam.data.length > 0) {
      const notasinfo = queryNotasInfoFam.data[0];
      reset({
        nota_ph: notasinfo.nota_ph || "",
        nota_c: notasinfo.nota_c || "",
        nota_h: notasinfo.nota_h || "",
        nota_f: notasinfo.nota_f || "",
      });
    }
  }, [queryNotasInfoFam.data, reset]);

  useEffect(() => {
    if (queryFecha.data && queryFecha.data.length > 0) {
      const registroValido = queryFecha.data.find((item) => item.fecha_infofam);

      if (registroValido) {
        setValue("fecha_infofam", registroValido.fecha_infofam);
      }
    }
  }, [queryFecha.data, setValue]);

  return (
    <div className="max-w-[800px] mx-auto p-4 bg-white">
      {/* Marca de agua */}
      <div className="absolute inset-0 flex justify-center items-center pointer-events-none z-0">
        <div
          className="text-blue-500 text-9xl font-bold transform rotate-[-48deg]"
          style={{ opacity: 0.1 }}
        >
          CONFIDENCIAL
        </div>
      </div>
      {/* Header */}
      <HeaderPrint titulo={tituloFormulario} />
      {/* Título del formulario */}
      <div id="headerContent" className="text-center">
        <h1
          className="text-black text-xl font-bold py-3 px-8 rounded-b-md inline-block shadow-lg flex justify-center transition-transform duration-300 ease-in-out hover:scale-105 cursor-pointer"
          style={{
            backgroundImage: `url(${fotoMapa})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {tituloFormulario}
        </h1>
      </div>
      <div className="space-y-0.5 p-1 mt-2">
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-1 flex items-center">
            <Label htmlFor="fecha_bienesinmuebles" className="mr-2 text-xs">
              FECHA VISITA:
            </Label>
            <Controller
              name="fecha_infofam"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextInput
                  id="fecha_infofam"
                  name="fecha_infofam"
                  type="date"
                  value={
                    queryFecha.data && queryFecha.data.length > 0
                      ? queryFecha.data[0].fecha_infofam || ""
                      : ""
                  }
                  disabled={true}
                  className="text-xs py-1 px-2"
                />
              )}
            />
          </div>
          <div className="col-span-2">
            <Label htmlFor="nombre" className="text-xs">
              NOMBRE:
            </Label>
            <TextInput
              id="nombre"
              name="nombre"
              value={`${queryEvaluado.data?.nombre || ""} ${
                queryEvaluado.data?.apellido_paterno || ""
              } ${queryEvaluado.data?.apellido_materno || ""}`}
              onChange={handleInputChange}
              disabled={true}
              className="text-xs py-1 px-2"
            />
          </div>
          {/* Campo para RFC */}
          <div className="col-span-1">
            <Label htmlFor="rfc" className="text-xs">
              RFC:
            </Label>
            <TextInput
              id="rfc"
              name="rfc"
              onChange={handleInputChange}
              value={queryEvaluado.data?.rfc || ""}
              disabled={true}
              className="text-xs py-1 px-2"
            />
          </div>
          {/* Edad */}
          <div className="col-span-1">
            <Label htmlFor="edad" className="text-xs">
              EDAD:
            </Label>
            <TextInput
              id="edad"
              name="edad"
              value={edad !== null ? edad.toString() : ""}
              disabled={true}
              className="text-xs py-1 px-2"
            />
          </div>
          <div className="col-span-1">
            <Label htmlFor="sexo" className="text-xs">
              SEXO:
            </Label>
            <TextInput
              id="sexo"
              name="sexo"
              value={generoTexto}
              disabled={true}
              className="text-xs py-1 px-2"
            />
          </div>
          <div className="col-span-1">
            <Label htmlFor="estadoCivil" className="text-xs">
              ESTADO CIVIL:
            </Label>
            <TextInput
              id="estadoCivil"
              name="estadoCivil"
              onChange={handleInputChange}
              value={`${queryEvaluado.data?.estado_civil || ""}`}
              disabled={true}
              className="text-xs py-1 px-2"
            />
          </div>
          <div className="col-span-1">
            <Label htmlFor="escolaridad" className="text-xs">
              ESCOLARIDAD:
            </Label>
            <TextInput
              id="escolaridad"
              name="escolaridad"
              onChange={handleInputChange}
              value={`${queryEvaluado.data?.escolaridad || ""}`}
              disabled={true}
              className="text-xs py-1 px-2"
            />
          </div>
          <div className="col-span-1 flex items-center">
            <Label htmlFor="fechaNacimiento" className="mr-2 text-xs">
              FECHA DE NACIMIENTO:
            </Label>
            <TextInput
              id="fechaNacimiento"
              name="fechaNacimiento"
              value={`${queryEvaluado.data?.fecha_nacimiento || ""}`}
              disabled={true}
              type="date"
              onChange={handleInputChange}
              className="w-32 text-xs py-1 px-2"
            />
          </div>
          <div className="col-span-1">
            <Label htmlFor="curp" className="text-xs">
              CURP:
            </Label>
            <TextInput
              id="curp"
              name="curp"
              onChange={handleInputChange}
              value={queryEvaluado.data?.curp || ""}
              disabled={true}
              className="text-xs py-1 px-2"
            />
          </div>
          <div className="col-span-1">
            <Label htmlFor="empresa" className="text-xs">
              EMPRESA:
            </Label>
            <TextInput
              id="empresa_id"
              name="empresa"
              value={queryEvaluado.data?.empresa_id?.nombre || ""}
              onChange={handleInputChange}
              disabled={true}
              className="text-xs py-1 px-2"
            />
          </div>
        </div>
      </div>
      <div className="space-y-6">
        <div>
          <div>
            <img
              src={BarraInfoFam}
              alt="HeaderDatosGenerales"
              className="rounded-lg"
            />
          </div>
          <div className="bg-blue-50 p-2 border border-gray-300 text-center font-medium text-gray-700 border-b border-gray-300">
            PADRES Y HERMANOS 1
          </div>
          <div className="relative">
            <table className="w-full border-collapse">
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    NOMBRE:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="nombre_ph1"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="nombre_ph1"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.nombre_ph1 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    PARENTESCO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="parentesco_ph1"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="parentesco_ph1"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.parentesco_ph1 ||
                                ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    EDAD Y ESTADO CIVIL:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="parentesco_ph1"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="parentesco_ph1"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? `${
                                  queryPadresHermanos.data[0]?.edad_ph1
                                } años de edad y es ${
                                  queryPadresHermanos.data[0]
                                    ?.estado_civilph1 || ""
                                }`
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    DOMICILIO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="domicilio_ph1"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="domicilio_ph1"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.domicilio_ph1 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    VIVE CON EL EVALUADO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="vivecon_evaluadoph1"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <select
                          id="vivecon_evaluadoph1"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.vivecon_evaluadoph1 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        >
                          <option value="true">Sí</option>
                          <option value="false">No</option>
                        </select>
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    ACTIVIDAD LABORAL:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="actividad_laboralph1"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="actividad_laboralph1"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.actividad_laboralph1 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    ULTIMO GRADO DE ESTUDIOS:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="ultimogrado_estudiosph1"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="ultimogrado_estudiosph1"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.ultimogrado_estudiosph1 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-[100px]"></div>
          <div className="bg-blue-50 p-2 border border-gray-300 text-center font-medium text-gray-700 border-b border-gray-300">
            PADRES Y HERMANOS 2
          </div>
          <div className="relative">
            <table className="w-full border-collapse">
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    NOMBRE:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="nombre_ph2"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="nombre_ph2"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.nombre_ph2 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    PARENTESCO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="parentesco_ph2"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="parentesco_ph2"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.parentesco_ph2 ||
                                ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    EDAD Y ESTADO CIVIL:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="parentesco_ph2"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="parentesco_ph2"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? `${
                                  queryPadresHermanos.data[0]?.edad_ph2
                                } años de edad y es ${
                                  queryPadresHermanos.data[0]
                                    ?.estado_civilph2 || ""
                                }`
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    DOMICILIO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="domicilio_ph2"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="domicilio_ph2"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.domicilio_ph2 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    VIVE CON EL EVALUADO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="vivecon_evaluadoph2"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <select
                          id="vivecon_evaluadoph2"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.vivecon_evaluadoph2 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        >
                          <option value="true">Sí</option>
                          <option value="false">No</option>
                        </select>
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    ACTIVIDAD LABORAL:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="actividad_laboralph2"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="actividad_laboralph2"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.actividad_laboralph2 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    ULTIMO GRADO DE ESTUDIOS:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="ultimogrado_estudiosph2"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="ultimogrado_estudiosph2"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.ultimogrado_estudiosph2 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-blue-50 p-2 border border-gray-300 text-center font-medium text-gray-700 border-b border-gray-300">
            PADRES Y HERMANOS 3
          </div>
          <div className="relative">
            <table className="w-full border-collapse">
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    NOMBRE:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="nombre_ph3"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="nombre_ph3"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.nombre_ph3 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    PARENTESCO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="parentesco_ph3"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="parentesco_ph3"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.parentesco_ph3 ||
                                ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    EDAD Y ESTADO CIVIL:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="parentesco_ph3"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="parentesco_ph3"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? `${
                                  queryPadresHermanos.data[0]?.edad_ph3
                                } años de edad y es ${
                                  queryPadresHermanos.data[0]
                                    ?.estado_civilph3 || ""
                                }`
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    DOMICILIO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="domicilio_ph3"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="domicilio_ph3"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.domicilio_ph3 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    VIVE CON EL EVALUADO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="vivecon_evaluadoph3"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <select
                          id="vivecon_evaluadoph3"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.vivecon_evaluadoph3 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        >
                          <option value="true">Sí</option>
                          <option value="false">No</option>
                        </select>
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    ACTIVIDAD LABORAL:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="actividad_laboralph3"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="actividad_laboralph3"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.actividad_laboralph3 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    ULTIMO GRADO DE ESTUDIOS:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="ultimogrado_estudiosph3"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="ultimogrado_estudiosph3"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.ultimogrado_estudiosph3 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-blue-50 p-2 border border-gray-300 text-center font-medium text-gray-700 border-b border-gray-300">
            PADRES Y HERMANOS 4
          </div>
          <div className="relative">
            <table className="w-full border-collapse">
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    NOMBRE:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="nombre_ph4"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="nombre_ph4"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.nombre_ph4 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    PARENTESCO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="parentesco_ph4"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="parentesco_ph4"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.parentesco_ph4 ||
                                ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    EDAD Y ESTADO CIVIL:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="parentesco_ph4"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="parentesco_ph4"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? `${
                                  queryPadresHermanos.data[0]?.edad_ph4
                                } años de edad y es ${
                                  queryPadresHermanos.data[0]
                                    ?.estado_civilph4 || ""
                                }`
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    DOMICILIO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="domicilio_ph4"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="domicilio_ph4"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.domicilio_ph4 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    VIVE CON EL EVALUADO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="vivecon_evaluadoph4"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <select
                          id="vivecon_evaluadoph4"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.vivecon_evaluadoph4 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        >
                          <option value="true">Sí</option>
                          <option value="false">No</option>
                        </select>
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    ACTIVIDAD LABORAL:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="actividad_laboralph4"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="actividad_laboralph4"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.actividad_laboralph4 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    ULTIMO GRADO DE ESTUDIOS:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="ultimogrado_estudiosph4"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="ultimogrado_estudiosph4"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.ultimogrado_estudiosph4 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-[50px]"></div>
          <div className="bg-blue-50 p-2 border border-gray-300 text-center font-medium text-gray-700 border-b border-gray-300">
            PADRES Y HERMANOS 5
          </div>
          <div className="relative">
            <table className="w-full border-collapse">
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    NOMBRE:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="nombre_ph5"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="nombre_ph5"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.nombre_ph5 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    PARENTESCO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="parentesco_ph5"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="parentesco_ph5"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.parentesco_ph5 ||
                                ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    EDAD Y ESTADO CIVIL:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="parentesco_ph5"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="parentesco_ph5"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? `${
                                  queryPadresHermanos.data[0]?.edad_ph5
                                } años de edad y es ${
                                  queryPadresHermanos.data[0]
                                    ?.estado_civilph5 || ""
                                }`
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    DOMICILIO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="domicilio_ph5"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="domicilio_ph5"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]?.domicilio_ph5 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    VIVE CON EL EVALUADO:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="vivecon_evaluadoph5"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <select
                          id="vivecon_evaluadoph5"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.vivecon_evaluadoph5 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        >
                          <option value="true">Sí</option>
                          <option value="false">No</option>
                        </select>
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    ACTIVIDAD LABORAL:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="actividad_laboralph5"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="actividad_laboralph5"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.actividad_laboralph5 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                    ULTIMO GRADO DE ESTUDIOS:{" "}
                  </td>
                  <td className="border border-gray-300 p-0.5 text-xs">
                    <Controller
                      name="ultimogrado_estudiosph5"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <input
                          type="text"
                          id="ultimogrado_estudiosph5"
                          value={
                            field.value ||
                            (queryPadresHermanos.data &&
                            queryPadresHermanos.data.length > 0
                              ? queryPadresHermanos.data[0]
                                  ?.ultimogrado_estudiosph5 || ""
                              : "")
                          }
                          disabled={true}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="text-left w-auto border rounded text-xs leading-tight"
                        />
                      )}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="border border-gray-300 mt-1">
        <div className="bg-blue-100 p-1 text-center font-semibold text-gray-700 border-b border-gray-300">
          NOTAS
        </div>
        <Controller
          name="ultimogrado_estudiosph"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <textarea
              className="w-full p-1 min-h-[80px] text-sm text-gray-900 border-0 focus:ring-0"
              aria-label="Notes"
              id="notes"
              name="notes"
              value={
                field.value ||
                (queryNotasInfoFam.data && queryNotasInfoFam.data.length > 0
                  ? queryNotasInfoFam.data[0]?.nota_ph || ""
                  : "")
              }
              disabled={true}
            />
          )}
        />
      </div>
      <div
        className="p-4 border-t border-gray-300"
        style={{ pageBreakInside: "avoid", marginBottom: "0" }}
      >
        <div className="mt-[350px]"></div>
        <div className="text-center">
          <p
            className="text-left text-xs text-gray-600"
            style={{ marginBottom: "0" }}
          >
            Bajo protesta de decir verdad, hago constar que los datos
            proporcionados son verdaderos. Asimismo, autorizo a la empresa SKA
            TRACKING AND SECURITY S.A. de C.V. para que proporcione toda la
            información, documentos y todos los datos que derive de las
            evaluaciones de pruebas de confianza que se me realicen.
          </p>
        </div>
        <div
          className="flex justify-end mt-2 -mb-2"
          style={{ pageBreakInside: "avoid", marginTop: "0" }}
        >
          <p className="text-right text-xs text-gray-600">
            ___________________________
            <br />
            Nombre y Firma del Evaluado
          </p>
        </div>
      </div>
      {/* Header */}
      <HeaderPrint titulo={tituloFormulario} />
      {/* Título del formulario */}
      <div id="headerContent" className="text-center">
        <h1
          className="text-black text-xl font-bold py-3 px-8 rounded-b-md inline-block shadow-lg flex justify-center transition-transform duration-300 ease-in-out hover:scale-105 cursor-pointer"
          style={{
            backgroundImage: `url(${fotoMapa})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {tituloFormulario}
        </h1>
      </div>
      {/* Marca de agua */}
      <div className="absolute inset-0 top-[150%] flex justify-center items-center pointer-events-none z-0">
        <div
          className="text-blue-500 text-9xl font-bold transform rotate-[-48deg]"
          style={{ opacity: 0.1 }}
        >
          CONFIDENCIAL
        </div>
      </div>
      <div className="bg-blue-50 p-2 border border-gray-300 text-center font-medium text-gray-700 border-b border-gray-300">
        CONYUGE
      </div>
      <div className="relative">
        <table className="w-full border-collapse">
          <tbody>
            <tr>
              <td className="border border-gray-300 p-1 font-semibold w-3/4 text-xs">
                NOMBRE:{" "}
              </td>
              <td className="border border-gray-300 p-1 text-sm">
                <Controller
                  name="nombrec"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="nombrec"
                      value={
                        field.value ||
                        (queryConyuge.data && queryConyuge.data.length > 0
                          ? queryConyuge.data[0]?.nombrec || ""
                          : "")
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-1 font-semibold w-3/4 text-xs">
                EDAD:{" "}
              </td>
              <td className="border border-gray-300 p-1 text-sm">
                <Controller
                  name="edadc"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="edadc"
                      value={
                        field.value ||
                        (queryConyuge.data && queryConyuge.data.length > 0
                          ? queryConyuge.data[0]?.edadc || ""
                          : "")
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-1 font-semibold w-3/4 text-xs">
                DOMICILIO:{" "}
              </td>
              <td className="border border-gray-300 p-1 text-sm">
                <Controller
                  name="domicilioc"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="domicilioc"
                      value={
                        field.value ||
                        (queryConyuge.data && queryConyuge.data.length > 0
                          ? queryConyuge.data[0]?.domicilioc || ""
                          : "")
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-1 font-semibold w-3/4 text-xs">
                VIVE CON EL EVALUADO:{" "}
              </td>
              <td className="border border-gray-300 p-1 text-sm">
                <Controller
                  name="vivecon_evaluadoc"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <select
                      id="vivecon_evaluadoc"
                      value={
                        field.value ||
                        (queryConyuge.data && queryConyuge.data.length > 0
                          ? queryConyuge.data[0]?.vivecon_evaluadoc || ""
                          : "")
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded"
                    >
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-1 font-semibold w-3/4 text-xs">
                ACTIVIDAD LABORAL:{" "}
              </td>
              <td className="border border-gray-300 p-1 text-sm">
                <Controller
                  name="actividad_laboralc"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="actividad_laboralc"
                      value={
                        field.value ||
                        (queryConyuge.data && queryConyuge.data.length > 0
                          ? queryConyuge.data[0]?.actividad_laboralc || ""
                          : "")
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-1 font-semibold w-3/4 text-xs">
                ÚLTIMO GRADO DE ESTUDIOS:{" "}
              </td>
              <td className="border border-gray-300 p-1 text-sm">
                <Controller
                  name="ultimogrado_estudiosc"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="ultimogrado_estudiosc"
                      value={
                        field.value ||
                        (queryConyuge.data && queryConyuge.data.length > 0
                          ? queryConyuge.data[0]?.ultimogrado_estudiosc || ""
                          : "")
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded"
                    />
                  )}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Sección de Notas */}
      <div className="border border-gray-300">
        <div className="bg-blue-100 p-2 text-center font-semibold text-gray-700 border-b border-gray-300">
          NOTAS
        </div>
        <Controller
          name="nota_c"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <textarea
              className="w-full p-2 min-h-[50px] text-sm text-gray-900 border-0 focus:ring-0"
              aria-label="Notes"
              value={
                field.value ||
                (queryNotasInfoFam.data && queryNotasInfoFam.data.length > 0
                  ? queryNotasInfoFam.data[0]?.nota_c || ""
                  : "")
              }
            />
          )}
        />
      </div>
      <div className="mt-[350px]"></div>
      <div className="bg-blue-50 p-2 border border-gray-300 text-center font-medium text-gray-700 border-b border-gray-300">
        HIJO 1
      </div>
      <div className="relative">
        <table className="w-full border-collapse">
          <tbody>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                NOMBRE:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="nombreh1"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="nombreh1"
                      value={field.value || queryHijos.data?.nombreh1 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                EDAD:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="edadh1"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="edadh1"
                      value={field.value || queryHijos.data?.edadh1 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                DOMICILIO:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="domicilioh1"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="domicilioh1"
                      value={field.value || queryHijos.data?.domicilioh1 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                VIVE CON EL EVALUADO:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="vivecon_evaluadoh1"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <select
                      id="vivecon_evaluadoh1"
                      value={
                        field.value || queryHijos.data?.vivecon_evaluadoh1 || ""
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    >
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                ULTIMO GRADO DE ESTUDIOS:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="ultimogrado_estudiosh1"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="ultimogrado_estudiosh1"
                      value={
                        field.value ||
                        queryHijos.data?.ultimogrado_estudiosh1 ||
                        ""
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="bg-blue-50 p-2 border border-gray-300 text-center font-medium text-gray-700 border-b border-gray-300">
        HIJO 2
      </div>
      <div className="relative">
        <table className="w-full border-collapse">
          <tbody>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                NOMBRE:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="nombreh2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="nombreh2"
                      value={field.value || queryHijos.data?.nombreh2 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                EDAD:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="edadh2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="edadh2"
                      value={field.value || queryHijos.data?.edadh2 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                DOMICILIO:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="domicilioh2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="domicilioh2"
                      value={field.value || queryHijos.data?.domicilioh2 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                VIVE CON EL EVALUADO:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="vivecon_evaluadoh2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <select
                      id="vivecon_evaluadoh2"
                      value={
                        field.value || queryHijos.data?.vivecon_evaluadoh2 || ""
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    >
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                ULTIMO GRADO DE ESTUDIOS:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="ultimogrado_estudiosh2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="ultimogrado_estudiosh2"
                      value={
                        field.value ||
                        queryHijos.data?.ultimogrado_estudiosh2 ||
                        ""
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="bg-blue-50 p-2 border border-gray-300 text-center font-medium text-gray-700 border-b border-gray-300">
        HIJO 3
      </div>
      <div className="relative">
        <table className="w-full border-collapse">
          <tbody>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                NOMBRE:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="nombreh3"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="nombreh3"
                      value={field.value || queryHijos.data?.nombreh3 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                EDAD:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="edadh3"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="edadh3"
                      value={field.value || queryHijos.data?.edadh3 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                DOMICILIO:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="domicilioh3"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="domicilioh3"
                      value={field.value || queryHijos.data?.domicilioh3 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                VIVE CON EL EVALUADO:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="vivecon_evaluadoh3"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <select
                      id="vivecon_evaluadoh3"
                      value={
                        field.value || queryHijos.data?.vivecon_evaluadoh3 || ""
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    >
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                ULTIMO GRADO DE ESTUDIOS:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="ultimogrado_estudiosh3"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="ultimogrado_estudiosh3"
                      value={
                        field.value ||
                        queryHijos.data?.ultimogrado_estudiosh3 ||
                        ""
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="bg-blue-50 p-2 border border-gray-300 text-center font-medium text-gray-700 border-b border-gray-300">
        HIJO 4
      </div>
      <div className="relative">
        <table className="w-full border-collapse">
          <tbody>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                NOMBRE:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="nombreh4"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="nombreh4"
                      value={field.value || queryHijos.data?.nombreh4 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                EDAD:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="edadh4"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="edadh4"
                      value={field.value || queryHijos.data?.edadh4 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                DOMICILIO:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="domicilioh4"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="domicilioh4"
                      value={field.value || queryHijos.data?.domicilioh4 || ""}
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                VIVE CON EL EVALUADO:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="vivecon_evaluadoh4"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <select
                      id="vivecon_evaluadoh4"
                      value={
                        field.value || queryHijos.data?.vivecon_evaluadoh4 || ""
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    >
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  )}
                />
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-0.5 font-semibold w-3/4 text-xs">
                ULTIMO GRADO DE ESTUDIOS:{" "}
              </td>
              <td className="border border-gray-300 p-0.5 text-xs">
                <Controller
                  name="ultimogrado_estudiosh4"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="text"
                      id="ultimogrado_estudiosh4"
                      value={
                        field.value ||
                        queryHijos.data?.ultimogrado_estudiosh4 ||
                        ""
                      }
                      disabled={true}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="text-left w-auto border rounded text-xs leading-tight"
                    />
                  )}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Sección de Notas */}
      <div className="border border-gray-300">
        <div className="bg-blue-100 p-2 text-center font-semibold text-gray-700 border-b border-gray-300">
          NOTAS
        </div>
        <Controller
          name="nota_h"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <textarea
              className="w-full p-2 min-h-[50px] text-sm text-gray-900 border-0 focus:ring-0"
              aria-label="Notes"
              value={
                field.value ||
                (queryNotasInfoFam.data && queryNotasInfoFam.data.length > 0
                  ? queryNotasInfoFam.data[0]?.nota_h || ""
                  : "")
              }
            />
          )}
        />
      </div>
      <div className="p-2 border-t border-gray-300">
        <div className="text-center">
          <p className="text-left text-xs text-gray-600">
            Bajo protesta de decir verdad, hago constar que los datos
            proporcionados son verdaderos. Asimismo, autorizo a la empresa SKA
            TRACKING AND SECURITY S.A. de C.V. para que proporcione toda la
            información, documentos y todos los datos que derive de las
            evaluaciones de pruebas de confianza que se me realicen.
          </p>
        </div>
        <div className="flex justify-end">
          <p className="text-right text-xs text-gray-600">
            ___________________________
            <br />
            Nombre y Firma del Evaluado
          </p>
        </div>
      </div>
    </div>
  );
};
